<template>
  <div class="who-page">
    <StepProgress />

    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" md="8" lg="6">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Who
            </v-card-title>
            
            <v-card-text class="pa-6">
              <v-form ref="form" v-model="valid">
                <!-- Person's name -->
                <div class="mb-6">
                  <v-text-field
                    v-model="personName"
                    label="What is the name of your loved one?"
                    variant="outlined"
                    density="comfortable"
                    :rules="nameRules"
                    required
                    class="mb-4"
                  ></v-text-field>
                </div>

                <!-- Profile photo upload -->
                <div class="mb-6">
                  <p class="text-body-1 mb-3">{{ profilePhotoLabel }}</p>

                  <div v-if="!profilePhotoPreview && !existingProfilePhotoUrl" class="photo-upload-zone" @click="triggerPhotoUpload">
                    <v-icon size="48" color="primary" class="mb-2">mdi-camera-plus</v-icon>
                    <p class="text-body-2">Click to upload photo</p>
                    <input
                      ref="photoInput"
                      type="file"
                      accept="image/*,.heic,.HEIC"
                      style="display: none"
                      @change="handlePhotoUpload"
                    />
                  </div>

                  <div v-else class="photo-preview-container">
                    <img :src="profilePhotoPreview || existingProfilePhotoUrl" alt="Profile photo" class="profile-photo-preview" />
                    <v-btn
                      icon
                      size="default"
                      color="error"
                      class="remove-photo-btn"
                      @click="removeProfilePhoto"
                    >
                      <v-icon size="22">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Life dates -->
                <v-row class="mb-6">
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="birthDate"
                      :label="birthDateLabel"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                      :max="deathDate || today"
                      :rules="birthDateRules"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="deathDate"
                      :label="deathDateLabel"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                      :min="birthDate || undefined"
                      :max="today"
                      :rules="deathDateRules"
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Event question -->
                <div class="mb-6">
                  <p class="text-body-1 mb-4">Is there an event to remember {{ personName.trim() || 'them' }}?</p>

                  <v-radio-group v-model="hasEventOption" class="mb-4">
                    <v-radio
                      label="Yes, there is an event"
                      value="yes"
                    ></v-radio>
                    <v-radio
                      label="No event at this time"
                      value="no"
                    ></v-radio>
                  </v-radio-group>

                  <!-- Event details (shown when "yes" is selected) -->
                  <v-expand-transition>
                    <div v-if="hasEventOption === 'yes'">
                      <v-text-field
                        v-model="eventName"
                        label="What is the event called? (Optional)"
                        variant="outlined"
                        density="comfortable"
                        placeholder="e.g., Funeral Service, Wake, Celebration of Life"
                        class="mb-4"
                      ></v-text-field>

                      <v-text-field
                        v-model="memorialDate"
                        label="Event Date"
                        type="date"
                        variant="outlined"
                        density="comfortable"
                        class="mb-4"
                      ></v-text-field>

                      <!-- Time fields -->
                      <v-row class="mb-4" no-gutters>
                        <v-col cols="6" class="pr-2">
                          <v-select
                            v-model="memorialHour"
                            :items="hourItems"
                            label="Hour (Optional)"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-select>
                        </v-col>
                        <v-col cols="6" class="pl-2">
                          <v-select
                            v-model="memorialMinute"
                            :items="minuteItems"
                            label="Minute (Optional)"
                            variant="outlined"
                            density="comfortable"
                            clearable
                          ></v-select>
                        </v-col>
                      </v-row>

                      <!-- Address field -->
                      <v-text-field
                        v-model="memorialAddress"
                        label="Do you have an address for the event? (Optional but map will show if you do)"
                        variant="outlined"
                        density="comfortable"
                        placeholder="e.g., St. Mary's Church, 123 Main Street, City, Australia"
                        class="mb-4"
                        @blur="onAddressBlur"
                      ></v-text-field>
                      
                      <!-- Additional details field -->
                      <v-textarea
                        v-model="memorialDetails"
                        label="Any other known details? (Optional)"
                        variant="outlined"
                        density="comfortable"
                        placeholder="e.g., Time: 2:00 PM, Dress code: Formal, Reception following at..."
                        rows="3"
                        class="mb-4"
                      ></v-textarea>
                      
                      <!-- Information note -->
                      <v-alert
                        type="info"
                        variant="tonal"
                        density="compact"
                        class="mb-4"
                      >
                        Don't worry if you don't have all the details now. You can come back and fill this in before it is shared with others for their contribution.
                      </v-alert>

                      <!-- Google Maps embed -->
                      <div v-if="showMap && memorialAddress && memorialAddress.trim()" class="map-container mb-4">
                        <iframe
                          :src="`https://www.google.com/maps?q=${encodeURIComponent(memorialAddress.trim())}&output=embed`"
                          width="100%"
                          height="300"
                          style="border:0; border-radius: 8px;"
                          allowfullscreen
                          loading="lazy"
                          referrerpolicy="no-referrer-when-downgrade"
                        ></iframe>
                      </div>
                    </div>
                  </v-expand-transition>
                </div>
              </v-form>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="previousStep"
                class="mr-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                :disabled="!canProceed || saving"
                :loading="saving"
                @click="showNextStep ? nextStep() : saveMemorial()"
                class="px-8"
              >
                {{ showNextStep ? 'Next Step' : 'Save' }}
                <v-icon right class="ml-2">{{ showNextStep ? 'mdi-arrow-right' : 'mdi-content-save' }}</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import StepProgress from '../components/StepProgress.vue'
import apiService from '../services/api'
import { isSupportedImage, normalizeImageFile, readFileAsDataUrl } from '../services/photoUpload'

const router = useRouter()
const store = useMemorialStore()

// Form data
const personName = ref('')
const birthDate = ref('')
const deathDate = ref('')
const hasEventOption = ref('no')
const eventName = ref('')
const memorialDate = ref('')
const memorialHour = ref(null)
const memorialMinute = ref(null)
const memorialAddress = ref('')
const memorialDetails = ref('')
const valid = ref(false)
const saving = ref(false)
const photoInput = ref(null)
const profilePhotoFile = ref(null)
const profilePhotoPreview = ref(null)
const existingProfilePhotoUrl = ref(null)
const showMap = ref(false)

const hourItems = Array.from({ length: 24 }, (_, i) => ({
  title: String(i).padStart(2, '0'),
  value: i
}))

const minuteItems = Array.from({ length: 60 }, (_, i) => ({
  title: String(i).padStart(2, '0'),
  value: i
}))

// Today, used to stop a birthdate being set in the future
const today = new Date().toISOString().split('T')[0]

// Form validation
const nameRules = [
  v => !!v || 'Name is required',
  v => (v && v.length >= 2) || 'Name must be at least 2 characters'
]

const birthDateRules = [
  v => !v || v <= today || 'Date of birth cannot be in the future',
  v => !v || !deathDate.value || v <= deathDate.value || 'Date of birth must be before date of death'
]

const deathDateRules = [
  v => !v || v <= today || 'Date of death cannot be in the future',
  v => !v || !birthDate.value || v >= birthDate.value || 'Date of death must be after date of birth'
]

// Computed properties
const canProceed = computed(() => {
  const datesAreValid = (!birthDate.value || birthDate.value <= today) &&
    (!deathDate.value || deathDate.value <= today) &&
    (!birthDate.value || !deathDate.value || birthDate.value <= deathDate.value)
  return personName.value && personName.value.length >= 2 && datesAreValid
})

const isEditMode = computed(() => {
  // User is in edit mode if they are logged in (completed account creation)
  // AND have an existing memorial they're editing
  // First-time users walking through the steps should see "Next Step" even with memorial UUID
  return store.isAccountCreated && store.isEmailVerified && store.memorialUuid
})

const showNextStep = computed(() => {
  return !isEditMode.value || store.maxReachedStep < 2
})

const profilePhotoLabel = computed(() => {
  if (personName.value && personName.value.trim()) {
    return `Photo of ${personName.value.trim()}`
  }
  return 'Photo of person'
})

const birthDateLabel = computed(() => {
  if (personName.value && personName.value.trim()) {
    return `Date of birth for ${personName.value.trim()} (Optional)`
  }
  return 'Date of birth (Optional)'
})

const deathDateLabel = computed(() => {
  if (personName.value && personName.value.trim()) {
    return `Date of death for ${personName.value.trim()} (Optional)`
  }
  return 'Date of death (Optional)'
})

// Watch for changes and update store
watch(personName, (newValue) => {
  store.updatePersonName(newValue)
})

watch(birthDate, (newValue) => {
  store.updateBirthDate(newValue || '')
})

watch(deathDate, (newValue) => {
  store.updateDeathDate(newValue || '')
})

watch(hasEventOption, (newValue) => {
  const hasEvent = newValue === 'yes'
  store.updateHasEvent(hasEvent)
  if (!hasEvent) {
    eventName.value = ''
    memorialDate.value = ''
    memorialHour.value = null
    memorialMinute.value = null
    memorialAddress.value = ''
    memorialDetails.value = ''
    store.updateEventName('')
    store.updateMemorialDate('')
    store.updateMemorialHour(null)
    store.updateMemorialMinute(null)
    store.updateMemorialAddress('')
    store.updateMemorialDetails('')
  }
})

watch(eventName, (newValue) => {
  store.updateEventName(newValue)
})

watch(memorialDate, (newValue) => {
  store.updateMemorialDate(newValue)
})

watch(memorialHour, (newValue) => {
  store.updateMemorialHour(newValue)
})

watch(memorialMinute, (newValue) => {
  store.updateMemorialMinute(newValue)
})

watch(memorialAddress, (newValue) => {
  store.updateMemorialAddress(newValue)
})

watch(memorialDetails, (newValue) => {
  store.updateMemorialDetails(newValue)
})

// Methods
const triggerPhotoUpload = () => {
  photoInput.value?.click()
}

const handlePhotoUpload = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  // Validate file type
  if (!isSupportedImage(file)) {
    console.error('Invalid file type. Please select an image.')
    return
  }

  // Validate file size (max 10MB)
  const maxSize = 10 * 1024 * 1024
  if (file.size > maxSize) {
    console.error('File size exceeds 10MB limit.')
    return
  }

  try {
    const processedFile = await normalizeImageFile(file)
    profilePhotoFile.value = processedFile
    existingProfilePhotoUrl.value = null // Clear existing photo when uploading new one

    // Create preview
    profilePhotoPreview.value = await readFileAsDataUrl(processedFile)

    // Store in memorial store
    store.updateProfilePhoto(processedFile)
  } catch (error) {
    console.error('Error processing file:', error)
  }
}

const onAddressBlur = () => {
  if (memorialAddress.value && memorialAddress.value.trim()) {
    showMap.value = true
  }
}

const removeProfilePhoto = () => {
  profilePhotoFile.value = null
  profilePhotoPreview.value = null
  existingProfilePhotoUrl.value = null
  if (photoInput.value) {
    photoInput.value.value = ''
  }
  store.updateProfilePhoto(null)
}

const nextStep = () => {
  if (canProceed.value) {
    store.goToStep(2) // Photos step
    router.push('/photos')
  }
}

const previousStep = () => {
  store.goToStep(0) // Welcome step
  router.push('/')
}

const saveMemorial = async () => {
  if (!canProceed.value || saving.value) return

  saving.value = true

  try {
    // Build memorial data from store
    const memorialData = {
      uuid: store.memorialUuid,
      userUuid: store.user?.uuid || null,
      adminUuids: null,
      nameOfPerson: store.personName || null,
      birthDate: store.birthDate || '',
      deathDate: store.deathDate || '',
      hasEvent: store.hasEvent,
      eventName: store.eventName || '',
      memorialDate: store.memorialDate || null,
      memorialHour: store.memorialHour ?? null,
      memorialMinute: store.memorialMinute ?? null,
      address: store.memorialAddress || null,
      details: store.memorialDetails || null,
      maxReachedStep: Math.max(store.maxReachedStep, 2),
      mediaUuids: null,
      stories: buildStoriesArray(),
      contacts: buildContactsArray(),
      hostMessage: store.personalMessage || null,
      hostVideoMessageUuid: null,
      stripeCustomerId: null
    }


    // Use create if no UUID exists, otherwise edit
    const response = store.memorialUuid
      ? await apiService.editMemorial(memorialData, store.apiToken)
      : await apiService.createMemorial(memorialData, store.apiToken)

    if (!response.success) {
      throw new Error(response.msg || 'Failed to save memory')
    }

    console.log('Memorial saved successfully:', response)

    // Store the memorial UUID if this was a new memorial
    if (response.memorial?.uuid && !store.memorialUuid) {
      store.setMemorialUuid(response.memorial.uuid)
      // Also update share slug if provided
      if (response.memorial.shareSlug) {
        store.setShareSlug(response.memorial.shareSlug)
      }
    }

    // Handle profile photo upload/update/delete
    if (store.memorialUuid) {
      try {
        if (profilePhotoFile.value) {
          // Upload new profile photo
          console.log('Uploading profile photo...')
          const profilePhotoResponse = await apiService.uploadPhoto(
            store.memorialUuid,
            profilePhotoFile.value,
            '', // No caption for profile photo
            store.apiToken,
            {
              isProfilePhoto: true
            }
          )

          if (!profilePhotoResponse.success) {
            throw new Error(profilePhotoResponse.msg || 'Failed to upload profile photo')
          }

          console.log('Profile photo uploaded successfully:', profilePhotoResponse)
        } else if (!profilePhotoPreview.value && !existingProfilePhotoUrl.value && store.profilePhoto) {
          // User removed the profile photo - update memorial to remove it
          console.log('Removing profile photo...')
          const updateResponse = await apiService.updateProfilePhoto(store.memorialUuid, '', store.apiToken)
          if (!updateResponse.success) {
            console.error('Failed to remove profile photo:', updateResponse.msg)
          } else {
            console.log('Profile photo removed successfully')
          }
        }
      } catch (profilePhotoError) {
        console.error('Profile photo operation error:', profilePhotoError)
        // Continue even if profile photo operation fails
      }
    }

    // Update change tracking after successful save
    const nextReachedStep = Math.max(store.maxReachedStep, 2)
    store.maxReachedStep = nextReachedStep
    await store.persistMaxReachedStep()
    store.markChangesSaved()

    // Show success message or redirect
    // For now, just log success and stay on page

  } catch (error) {
    console.error('Memorial save error:', error)
    // TODO: Show error message to user
  } finally {
    saving.value = false
  }
}

const buildStoriesArray = () => {
  return store.stories
    .filter(story => story && story.trim())
    .map(story => ({
      uuid: null,
      text: story.trim()
    }))
}

const buildContactsArray = () => {
  return store.contacts
    .filter(contact => contact && contact.trim())
    .map(contact => {
      const contactData = {
        uuid: null,
        email: null,
        phone: null,
        firstName: null,
        surname: null
      }
      
      // Check if it's an email or phone
      if (/.+@.+\..+/.test(contact.trim())) {
        contactData.email = contact.trim()
      } else if (/^[+]?[\s\-()]*([0-9][\s\-()]*){7,}/.test(contact.trim())) {
        contactData.phone = contact.trim()
      }
      
      return contactData
    })
    .filter(contact => contact.email || contact.phone) // Only include valid contacts
}

// Initialize from store on mount
onMounted(async () => {
  // If user has a memorial UUID, reload from server to ensure fresh data
  if (store.memorialUuid && store.isAccountCreated && store.isEmailVerified) {
    console.log('Reloading memorial data from server...')
    const loaded = await store.loadUserMemorialFromServer()
    if (loaded) {
      console.log('Memorial data reloaded successfully')
    }
  }

  personName.value = store.personName
  birthDate.value = store.birthDate
  deathDate.value = store.deathDate
  eventName.value = store.eventName
  memorialDate.value = store.memorialDate
  memorialHour.value = store.memorialHour ?? null
  memorialMinute.value = store.memorialMinute ?? null
  memorialAddress.value = store.memorialAddress
  memorialDetails.value = store.memorialDetails

  // If any event information exists, show the event fields so they can be edited
  if (store.eventName || store.memorialDate || store.memorialAddress || store.memorialDetails) {
    hasEventOption.value = 'yes'
  } else {
    hasEventOption.value = store.hasEvent ? 'yes' : 'no'
  }

  // Show map immediately if address already exists
  if (memorialAddress.value && memorialAddress.value.trim()) {
    showMap.value = true
  }

  // Load existing profile photo from store
  if (store.profilePhoto && store.profilePhoto.url) {
    existingProfilePhotoUrl.value = store.profilePhoto.url
  }

  // Restore locally uploaded profile photo (for non-logged-in users)
  if (store.profilePhotoFile) {
    profilePhotoFile.value = store.profilePhotoFile
    // Recreate preview from the stored file
    const reader = new FileReader()
    reader.onload = (e) => {
      profilePhotoPreview.value = e.target.result
    }
    reader.readAsDataURL(store.profilePhotoFile)
  }

  store.goToStep(1) // Set current step to Who

  // Initialize page tracking for logged-in users
  if (store.isAccountCreated && store.isEmailVerified && store.apiToken) {
    store.enterPage('who')
  }
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;
.who-page {
  @include page-container;
}

.main-card {
  @include main-card;
  
  .v-card-title {
    @include section-header;
  }
}

.v-radio-group {
  margin-top: 0;
}

.v-radio {
  margin-bottom: 8px;
}

.photo-upload-zone {
  border: 1px dashed rgba(var(--v-theme-primary), 0.3);
  border-radius: 8px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(var(--v-theme-primary), 0.02);

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.6);
    background: rgba(var(--v-theme-primary), 0.05);
  }
}

.photo-preview-container {
  position: relative;
  display: inline-block;
}

.profile-photo-preview {
  max-width: 300px;
  max-height: 300px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.remove-photo-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--v-theme-surface) !important;
  border-radius: 999px;
  min-width: 36px;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(15, 23, 42, 0.2);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
}

@include mobile {
  .v-card-title {
    font-size: 1.5rem !important;
  }

  .pa-6 {
    padding: 1rem !important;
  }

  .profile-photo-preview {
    max-width: 100%;
  }
}
</style>
